<template>
  <input
    type="text"
    :inputmode="decimalPlaces > 0 ? 'decimal' : 'numeric'"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :value="displayValue"
    @focus="onFocus"
    @blur="onBlur"
    @input="onInput"
  />
</template>

<script lang="ts">
import { Vue, Prop, Emit, Watch } from "vue-property-decorator";

export default class Mo_CurrencyInput extends Vue {
  // ==== Props ====
  @Prop({ default: "" }) readonly modelValue!: number | string;
  @Prop({ default: 0 }) readonly decimalPlaces!: number;
  @Prop({ default: false }) readonly allowNegative!: boolean;
  @Prop({ default: "ja-JP" }) readonly locale!: string;
  @Prop({ default: true }) readonly useGrouping!: boolean;
  @Prop({ default: null }) readonly min!: number | null;
  @Prop({ default: null }) readonly max!: number | null;
  @Prop({ default: "" }) readonly placeholder!: string;
  @Prop({ default: false }) readonly disabled!: boolean;
  @Prop({ default: false }) readonly readonly!: boolean;
  @Prop({ default: false }) readonly showValue!: boolean;

  // ---- データ（型注釈は付けず推論に任せる）----
  isFocused = false;
  raw = "";

  // ---- 初期化＆watch（デコレータなしで $watch を使う）----
  created() {
    this.raw = this.normalizeInitial((this.$props as any).modelValue);
    this.$watch(
      () => (this.$props as any).modelValue,
      (newVal: number | string | null) => {
        const norm = this.normalizeInitial(newVal);
        if (norm !== this.raw && !this.isFocused) this.raw = norm;
      },
      { immediate: true }
    );
  }

  // ---- 表示値（フォーカス外はカンマ表示）----
  get displayValue(): string {
    if (this.isFocused) return this.raw;
    if (
      this.raw === "" ||
      this.raw === "-" ||
      this.raw === "." ||
      this.raw === "-."
    )
      return "";
    const num = Number(this.raw);
    if (Number.isNaN(num)) return "";
    const { locale, useGrouping, decimalPlaces } = this.$props as any;
    return new Intl.NumberFormat(locale, {
      useGrouping,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(num);
  }

  // ---- ユーティリティ ----
  normalizeInitial(v: number | string | null | undefined): string {
    if (v === null || v === undefined) return "";
    const s = String(v).trim();
    const { decimalPlaces } = this.$props as any;
    const re = decimalPlaces > 0 ? /^-?\d*(\.\d*)?$/ : /^-?\d*$/;
    return re.test(s) ? s : this.stripToAllowed(s);
  }

  stripToAllowed(s: string): string {
    const { allowNegative, decimalPlaces } = this.$props as any;
    let sign = "";
    if (allowNegative && s.replace(/\s/g, "").startsWith("-")) sign = "-";
    let body = s.replace(/[^0-9.]/g, "");
    if (decimalPlaces === 0) {
      body = body.replace(/\./g, "");
    } else {
      const firstDot = body.indexOf(".");
      if (firstDot !== -1) {
        body =
          body.slice(0, firstDot + 1) +
          body.slice(firstDot + 1).replace(/\./g, "");
      }
    }
    return sign + body;
  }

  clampNumber(n: number): number {
    const { min, max } = this.$props as any;
    if (min !== null && !Number.isNaN(min)) n = Math.max(n, min);
    if (max !== null && !Number.isNaN(max)) n = Math.min(n, max);
    return n;
  }

  emitModel() {
    // 空 or 未確定記号は null を返す
    if (
      this.raw === "" ||
      this.raw === "-" ||
      this.raw === "." ||
      this.raw === "-."
    )
      return this.$emit("update:modelValue", null);

    let num = Number(this.raw);
    if (Number.isNaN(num)) return this.$emit("update:modelValue", null);

    const { decimalPlaces } = this.$props as any;
    if (decimalPlaces >= 0) {
      const p = Math.pow(10, decimalPlaces);
      num = Math.round(num * p) / p;
    }
    num = this.clampNumber(num);
    this.$emit("update:modelValue", num);
  }

  // ---- イベント ----
  onFocus(e: FocusEvent) {
    this.isFocused = true;
    const normalized = this.stripToAllowed(String(this.raw));
    const n = Number(normalized);
    if (!Number.isNaN(n) && n === 0) {
      this.raw = "";
    } else {
      this.raw = normalized;
    }
    this.$emit("focus", e);
  }

  onBlur(e: FocusEvent) {
    const input = e.target as HTMLInputElement;
    const value = input.value.trim();

    // 入力が空ならそのまま
    if (value === "") {
      if (this.showValue) {
        this.raw = "0";
        input.value = "0";
        this.$emit("update:modelValue", 0);
      } else {
        this.$emit("update:modelValue", null);
      }
      return;
    }

    // 整数チェック
    const isInteger = /^\d+$/.test(value);

    if (isInteger) {
      this.raw = value;
    } else {
      // 整数でなければ空白にする
      this.raw = this.showValue ? "0" : "";
      input.value = this.showValue ? "0" : "";
      this.$emit("update:modelValue", this.showValue ? 0 : null);
      return;
    }
    const { decimalPlaces } = this.$props as any;
    this.isFocused = false;

    if (this.showValue) {
      let next = this.stripToAllowed(String(this.raw));
      const num = Number(next);

      if (next.trim() === "" || Number.isNaN(num) || num === 0) {
        next = "0";
      } else {
        if (decimalPlaces === 0) next = next.replace(/\./g, "");
        else if (next.includes(".")) {
          const [i, d] = next.split(".");
          next = i + "." + d.slice(0, decimalPlaces);
        }
      }
      this.raw = next;
      this.$emit("input-raw", this.raw);
      this.emitModel();
      this.$emit("blur", e);
    } else {
      this.raw = this.stripToAllowed(String(this.raw));
      if (this.raw !== "" && decimalPlaces > 0) {
        const [i, d = ""] = this.raw.split(".");
        const fixed = d.padEnd(decimalPlaces, "0").slice(0, decimalPlaces);
        this.raw = `${i}.${fixed}`;
      }
      this.emitModel();
      this.$emit("blur", e);
    }
  }

  onInput(e: Event) {
    const { allowNegative, decimalPlaces } = this.$props as any;
    const t = e.target as HTMLInputElement;
    let next = this.stripToAllowed(t.value);
    if (!allowNegative) next = next.replace(/^-/, "");
    if (decimalPlaces === 0) next = next.replace(/\./g, "");
    else if (next.includes(".")) {
      const [i, d] = next.split(".");
      next = i + "." + d.slice(0, decimalPlaces);
    }
    this.raw = next;
    this.$emit("input-raw", this.raw);
    this.emitModel();
  }
}
</script>
